import { create } from "zustand";
import { send_request } from "../../utils/Request";

const BASE_PATH = "ai-service/mcp/server";

// send_request returns an Axios error object (with .response) on HTTP failure
// instead of throwing in most paths — mirror the convention used by
// useCustomFunctionsStore so both success and error shapes are handled.
function isErrorResponse(response) {
  if (!response) return true;
  if (response instanceof Error) return true;
  if (response.isAxiosError) return true;
  if (response.response) return true;
  return false;
}

function extractErrorMessage(source, fallback) {
  const data = source?.response?.data ?? source?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    if (typeof data.error === "string" && data.error.trim())
      return data.error.trim();
    if (typeof data.message === "string" && data.message.trim())
      return data.message.trim();
  }
  if (typeof source?.message === "string" && source.message.trim()) {
    return source.message.trim();
  }
  return fallback;
}

const useMcpServersStore = create((set, get) => ({
  servers: [],
  loading: false,
  error: null,

  clearError: () => set({ error: null }),

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const response = await send_request(BASE_PATH, "", {}, "GET");
      if (isErrorResponse(response)) {
        set({
          loading: false,
          error: extractErrorMessage(response, "Failed to load MCP servers"),
        });
        return [];
      }
      const data = Array.isArray(response?.data) ? response.data : [];
      set({ servers: data, loading: false });
      return data;
    } catch (err) {
      set({
        loading: false,
        error: extractErrorMessage(err, "Failed to load MCP servers"),
      });
      return [];
    }
  },

  // Shared body for the CRUD mutations: issues the request, normalizes the
  // success/error shapes, refetches the list, and surfaces a thrown Error so
  // callers can render inline failures. `test` is intentionally NOT routed
  // through this — it returns the response body on failure instead of throwing.
  runMutation: async (request, fallback) => {
    try {
      const response = await request();
      if (isErrorResponse(response)) {
        const msg = extractErrorMessage(response, fallback);
        set({ error: msg });
        throw new Error(msg);
      }
      await get().fetchAll();
      return response?.data ?? null;
    } catch (err) {
      const msg = extractErrorMessage(err, fallback);
      set({ error: msg });
      throw new Error(msg);
    }
  },

  // payload: { name, url, headers: [{ name, valuePrefix, secret, value, credentialId }] }
  create: (payload) =>
    get().runMutation(
      () => send_request(BASE_PATH, payload, {}, "POST"),
      "Failed to create MCP server"
    ),

  update: (serverId, payload) =>
    get().runMutation(
      () => send_request(`${BASE_PATH}/${serverId}`, payload, {}, "PUT"),
      "Failed to update MCP server"
    ),

  remove: (serverId) =>
    get().runMutation(
      () => send_request(`${BASE_PATH}/${serverId}`, "", {}, "DELETE"),
      "Failed to delete MCP server"
    ),

  refresh: (serverId) =>
    get().runMutation(
      () => send_request(`${BASE_PATH}/${serverId}/refresh`, "", {}, "POST"),
      "Failed to refresh tools"
    ),

  // Non-persisting connection test. payload accepts EITHER credentialId OR a
  // raw secretValue typed in the dialog before the credential is saved.
  // Returns the McpTestResult body { ok, toolCount, tools, error } regardless
  // of HTTP status so the dialog can render inline failures.
  test: async (payload) => {
    try {
      const response = await send_request(
        `${BASE_PATH}/test`,
        payload,
        {},
        "POST"
      );
      if (isErrorResponse(response)) {
        const data = response?.response?.data;
        if (data && typeof data === "object" && typeof data.ok === "boolean") {
          return data;
        }
        return {
          ok: false,
          toolCount: 0,
          tools: [],
          error: extractErrorMessage(response, "Test failed"),
        };
      }
      return (
        response?.data ?? {
          ok: false,
          toolCount: 0,
          tools: [],
          error: "Test failed",
        }
      );
    } catch (err) {
      return {
        ok: false,
        toolCount: 0,
        tools: [],
        error: extractErrorMessage(err, "Test failed"),
      };
    }
  },

  // Begins an OAuth connection. payload: { name, url, clientId?, clientSecret? }.
  // Returns { authorizationUrl, serverId } so the caller can open the consent popup.
  startOauth: async (payload) => {
    // withCredentials so the browser stores the HttpOnly binding cookie the backend
    // sets on this response; the OAuth callback later requires it to confirm the same
    // browser that started the flow is the one completing it (CSRF protection).
    const response = await send_request(
      `${BASE_PATH}/oauth/start`,
      payload,
      {},
      "POST",
      undefined,
      undefined,
      undefined,
      undefined,
      true
    );
    if (isErrorResponse(response)) {
      const msg = extractErrorMessage(response, "Failed to start authorization");
      set({ error: msg });
      throw new Error(msg);
    }
    return response?.data ?? null;
  },

  // Re-authorizes an existing OAuth connection. Returns { authorizationUrl, serverId }.
  reauthorizeOauth: async (serverId) => {
    // withCredentials: see startOauth — stores the binding cookie for the callback check.
    const response = await send_request(
      `${BASE_PATH}/oauth/${serverId}/reauthorize`,
      "",
      {},
      "POST",
      undefined,
      undefined,
      undefined,
      undefined,
      true
    );
    if (isErrorResponse(response)) {
      const msg = extractErrorMessage(response, "Failed to start re-authorization");
      set({ error: msg });
      throw new Error(msg);
    }
    return response?.data ?? null;
  },

  // Reads the public OAuth callback URL (for BYO-client redirect_uri setup).
  fetchOauthCallbackUrl: async () => {
    try {
      const response = await send_request(
        `${BASE_PATH}/oauth/callback-url`,
        "",
        {},
        "GET"
      );
      if (isErrorResponse(response)) return "";
      return response?.data?.callbackUrl ?? "";
    } catch (_err) {
      return "";
    }
  },
}));

export default useMcpServersStore;
